/** @module @ignore */
import * as React from 'react';
import { FeatureCollection } from 'geojson';
import { Layer, MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';

import { POINTS_LAYER, POLYGON_CIRCLE_COLOR, POLYGON_CIRCLE_STROKE_COLOR } from '../EditorConfig';
import { PolygonUtils } from '../../util/PolygonUtils';
import { IPoint } from '../../types/IPoint';

interface ICircleEditorPointProps {
  point: IPoint;
  radius: number;
  /** Fired when points change. */
  onChange: (point: IPoint, radius: number) => void;
}

let point: IPoint | null = null;
let radius: number | null = null;

const CircleEditorPoint = (props: ICircleEditorPointProps) => {
  const map = useMap().current!;
  let dragging: boolean = false;

  const handleMouseEnter = (e: MapMouseEvent) => {
    if(!dragging) map.getCanvas().style.cursor = 'nesw-resize';
  }

  const handleMouseLeave = (e: MapMouseEvent) => {
    if(!dragging) map.getCanvas().style.cursor = '';
  }

  const handleMouseDown = (e: MapMouseEvent) => {
    dragging = true;
    map.getCanvas().style.cursor = 'move';
    map.getMap().dragPan.disable();
  }

  const handleMouseUp = (e: MapMouseEvent) => {
    dragging = false;
    map.getCanvas().style.cursor = '';
    map.getMap().dragPan.enable();
  }

  const handleMouseMove = (e: MapMouseEvent) => {
    if(!dragging || !point) return;
    let newRadius = PolygonUtils.distance(point, e.lngLat);
    props.onChange(point, newRadius);
  }

  const handleClick = (e: MapMouseEvent) => {
    e.preventDefault();
  }

  const mount = () => {
    // Register for clicks and moves:
    map.on('mouseenter', POINTS_LAYER, handleMouseEnter);
    map.on('mouseleave', POINTS_LAYER, handleMouseLeave);
    map.on('mousedown', POINTS_LAYER, handleMouseDown);
    map.on('mouseup', handleMouseUp);
    map.on('mousemove', handleMouseMove);
    map.on('click', POINTS_LAYER, handleClick);
  }

  const unmount = () => {
    // Unregister for clicks and moves:
    map.off('mouseleave', POINTS_LAYER, handleMouseLeave);
    map.off('mouseenter', POINTS_LAYER, handleMouseEnter);
    map.off('mousedown', POINTS_LAYER, handleMouseDown);
    map.off('mouseup', handleMouseUp);
    map.off('mousemove', handleMouseMove);
    map.off('click', POINTS_LAYER, handleClick);
  }

  // Mount/unmount only once:
  React.useEffect(() => {
    mount();
    return unmount
  }, []);

  // Force the control to update when points change:
  React.useEffect(() => {
    point = { ...props.point };
    radius = props.radius;
  }, [props.point, props.radius]);

  const getJSON = (): FeatureCollection => {
    const lng = props.point.lng + (props.radius / 6371000) * (180 / Math.PI) / Math.cos(props.point.lat * Math.PI/180);
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [lng, props.point.lat]
        }
      }]
    }
  }  

  return (
    <Source type="geojson" data={getJSON()}>
      <Layer 
        id={POINTS_LAYER}
        type="circle"
        paint={{
          "circle-color": POLYGON_CIRCLE_COLOR,
          "circle-stroke-color": POLYGON_CIRCLE_STROKE_COLOR,
          "circle-radius": 5,
          "circle-stroke-width": 2
        }}
      />
    </Source>
  );
}

export { CircleEditorPoint }

